/**
 * A step that fails instead of hanging (Sam, 2026-11-04: the Mistakes tab's All window sat on its skeleton because one
 * page's request stalled in the browser, before the server, and nothing timed out, so it neither failed nor retried).
 * Pure.
 */

/** `step`, or a rejection with `name` as its message once `ms` pass without it settling. */
export function withTimeout<T>(step: Promise<T>, ms: number, name: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const late = new Promise<never>((_, reject) => { timer = setTimeout(() => reject(new Error(name)), ms); });
  return Promise.race([step, late]).finally(() => clearTimeout(timer));
}

/** fetch and its JSON body, aborted once `ms` pass: the abort covers the body too, so a response that stalls
 *  mid-download fails as well (the message is "REQUEST_TIMEOUT"). */
export async function timedFetchJson(url: string, init: RequestInit, ms: number): Promise<{ response: Response; data: Record<string, unknown> }> {
  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), ms);
  try {
    const response = await fetch(url, { ...init, signal: abort.signal });
    return { response, data: (await response.json()) as Record<string, unknown> };
  } catch (err) {
    // our timer fired: name it, whatever the browser rejected with (an AbortError, or nothing in older ones)
    throw abort.signal.aborted ? new Error("REQUEST_TIMEOUT") : err;
  } finally {
    clearTimeout(timer);
  }
}
